import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { PlannedAndReportedTimeService } from '@wilson/activities/reports';
import { findLatestArrivalBasedOnDeviationTime } from '@wilson/activities/util';
import {
  ActivityDeviationStatus,
  ResolvedActivity,
  ResolvedActivityReport,
  Service,
  Shift,
} from '@wilson/interfaces';
import { ReportedTimeService } from '@wilson/non-domain-specific/shift-helpers/core';
import { AgRendererComponent } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { addMinutes } from 'date-fns';

@Component({
  selector: 'wilson-end-time-renderer',
  template: `
    <div class="time-tag-wrapper">
      <span class="text-dark">
        {{ cellValue ? (cellValue | localizedDateTimeFormat) : '---' }}
      </span>
      <wilson-activity-time-tag
        [activityTimeDifference]="deviatedMins"
        [activityTimeStatus]="
          reportedTime
            ? ActivityDeviationStatus.Reported
            : ActivityDeviationStatus.Deviated
        "
        [accurateTime]="reportedTime || newDeviatedHour"
      ></wilson-activity-time-tag>
    </div>
  `,
  styles: [
    `
      span {
        line-height: 10px;
      }

      .time-tag-wrapper {
        text-wrap: wrap;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EndTimeRendererComponent implements AgRendererComponent {
  private readonly reportedTimeService = inject(ReportedTimeService);
  private readonly plannedAndReportedTimeService = inject(
    PlannedAndReportedTimeService,
  );

  public cellValue!: string | null;
  public reportedTime!: string | null;
  public deviatedMins!: number | null;
  public newDeviatedHour!: string | null;
  public ActivityDeviationStatus = ActivityDeviationStatus;

  agInit(params: ICellRendererParams<Service | Shift, string>): void {
    this.init(params);
  }

  refresh(params: ICellRendererParams<Service | Shift, string>): boolean {
    this.init(params);
    return true;
  }

  private init(params: ICellRendererParams<Service | Shift, string>): void {
    this.cellValue = params?.value ?? null;
    this.reportedTime = this.getReportedEndTime(params);
    const arrivalDeviationRecord = findLatestArrivalBasedOnDeviationTime(
      params.data?.activities ?? null,
    );

    if (arrivalDeviationRecord?.arrivalDeviation) {
      this.cellValue = arrivalDeviationRecord.endDatetime;
      this.deviatedMins =
        arrivalDeviationRecord.arrivalDeviation.deviationInMinutes;
      this.newDeviatedHour = this.deviatedMins
        ? addMinutes(new Date(this.cellValue), this.deviatedMins).toString()
        : null;
    }
  }

  getReportedEndTime(params: ICellRendererParams): string | null {
    const activities = params?.data?.activities;
    const activityReports = this.lastReportedActivityReports(activities);
    const reportedEndTime =
      this.plannedAndReportedTimeService.reportedEndTime(activityReports);
    return reportedEndTime;
  }

  private lastReportedActivityReports(
    activities: ResolvedActivity[],
  ): ResolvedActivityReport[] {
    activities = this.reportedTimeService.reportedActivities(activities);
    const lastActivity = activities[activities.length - 1];
    return lastActivity?.activityReports ?? [];
  }
}
